/**
 * BatchSelectionDialog - Popup for selecting product batches in sales
 * 
 * Shows available batches with MRP, expiry, quantity info.
 * User can select quantities from multiple batches.
 * Each selected batch becomes a separate line item in the sale.
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Package, Calendar, AlertTriangle, Plus, Minus, ShoppingCart } from "lucide-react";

// Format expiry date nicely
const formatExpiry = (expiryDate) => {
  if (!expiryDate) return "N/A";
  try {
    const d = new Date(expiryDate);
    if (isNaN(d)) return "N/A";
    return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
  } catch {
    return "N/A";
  }
};

// Get expiry status styling
const getExpiryStatus = (daysLeft, status) => {
  if (status === "expired" || daysLeft < 0) {
    return { color: "bg-red-100 text-red-700 border-red-300", label: "EXPIRED", isExpired: true };
  }
  if (status === "expiring_soon" || (daysLeft !== null && daysLeft <= 30)) {
    return { color: "bg-orange-100 text-orange-700 border-orange-300", label: `${daysLeft}d left`, isExpired: false };
  }
  if (status === "expiring_3months" || (daysLeft !== null && daysLeft <= 90)) {
    return { color: "bg-yellow-100 text-yellow-800 border-yellow-300", label: `${daysLeft}d left`, isExpired: false };
  }
  if (daysLeft !== null) {
    return { color: "bg-emerald-100 text-emerald-700 border-emerald-300", label: `${daysLeft}d left`, isExpired: false };
  }
  return { color: "bg-gray-100 text-gray-600 border-gray-300", label: "N/A", isExpired: false };
};

export default function BatchSelectionDialog({
  open,
  onOpenChange,
  product,
  batches = [],
  defaultDiscountPct = 5,
  onConfirm,
}) {
  // State for selected quantities per batch
  const [selections, setSelections] = useState({});
  // Format: { batchId: quantity }

  // Reset selections when dialog opens or product changes
  useEffect(() => {
    if (open) {
      setSelections({});
    }
  }, [open, product?.id]);

  // Filter batches with quantity > 0 (allow expired batches now)
  const availableBatches = useMemo(() => {
    return (batches || []).filter(b => b.quantity > 0);
  }, [batches]);

  // Calculate total selected quantity
  const totalSelected = useMemo(() => {
    return Object.values(selections).reduce((sum, qty) => sum + (qty || 0), 0);
  }, [selections]);

  // Update quantity for a batch
  const updateBatchQty = (batchId, newQty, maxQty) => {
    const qty = Math.max(0, Math.min(parseInt(newQty, 10) || 0, maxQty));
    setSelections(prev => {
      if (qty === 0) {
        const next = { ...prev };
        delete next[batchId];
        return next;
      }
      return { ...prev, [batchId]: qty };
    });
  };

  // Increment/decrement helpers
  const increment = (batchId, maxQty) => {
    const current = selections[batchId] || 0;
    if (current < maxQty) {
      setSelections(prev => ({ ...prev, [batchId]: current + 1 }));
    }
  };

  const decrement = (batchId) => {
    const current = selections[batchId] || 0;
    if (current > 0) {
      setSelections(prev => {
        const next = { ...prev, [batchId]: current - 1 };
        if (next[batchId] === 0) delete next[batchId];
        return next;
      });
    }
  };

  // Handle confirm - create line items for each selected batch
  const handleConfirm = () => {
    const lineItems = [];
    
    for (const [batchId, quantity] of Object.entries(selections)) {
      if (quantity > 0) {
        const batch = availableBatches.find(b => b.id === batchId);
        if (batch) {
          const mrp = Number(batch.mrp || product?.mrp || 0);
          const unitPrice = Math.round(mrp * (1 - defaultDiscountPct / 100) * 100) / 100;
          const expiryStatus = getExpiryStatus(batch.days_until_expiry, batch.expiry_status);
          
          lineItems.push({
            product_id: product.id,
            product_snapshot: product,
            batch_id: batchId,
            batch_info: {
              mrp: batch.mrp,
              expiry_date: batch.expiry_date,
              days_until_expiry: batch.days_until_expiry,
              expiry_status: batch.expiry_status,
            },
            quantity,
            mrp,
            unit_price: unitPrice,
            line_discount_pct: defaultDiscountPct,
            price_type: "retail",
            is_expired: expiryStatus.isExpired,
          });
        }
      }
    }
    
    if (lineItems.length > 0) {
      onConfirm(lineItems);
      onOpenChange(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="pb-2 border-b">
          <DialogTitle className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#9E6D5B]/10 flex items-center justify-center shrink-0">
              <Package className="w-5 h-5 text-[#9E6D5B]" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-base leading-tight truncate">
                {product.product}
              </div>
              <div className="text-xs text-muted-foreground font-normal mt-0.5">
                {product.brand_name} · {product.auto_barcode}
              </div>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Select batch quantities for {product.product}. Each batch has a different MRP and expiry date.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" />
            Select Batch & Quantity
          </div>

          {availableBatches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No available batches</p>
              <p className="text-xs mt-1">All batches are either expired or out of stock</p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableBatches.map((batch) => {
                const expiryStatus = getExpiryStatus(batch.days_until_expiry, batch.expiry_status);
                const selected = selections[batch.id] || 0;
                const isSelected = selected > 0;
                
                return (
                  <div
                    key={batch.id}
                    className={`border rounded-lg p-3 transition-all ${
                      isSelected 
                        ? "border-[#9E6D5B] bg-[#9E6D5B]/5 shadow-sm" 
                        : "border-border hover:border-[#9E6D5B]/40"
                    }`}
                    data-testid={`batch-option-${batch.id}`}
                  >
                    {/* Row 1: MRP and Expiry Badge */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-base">
                          ₹{Number(batch.mrp || 0).toFixed(0)}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] px-1.5 py-0 ${expiryStatus.color}`}
                        >
                          {expiryStatus.label}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Exp: <span className="font-mono">{formatExpiry(batch.expiry_date)}</span>
                      </span>
                    </div>

                    {/* Row 2: Available Qty and Selector */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Available: <span className="font-mono font-semibold text-foreground">{batch.quantity}</span>
                      </span>

                      {/* Quantity Selector */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => decrement(batch.id)}
                          disabled={selected === 0}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          max={batch.quantity}
                          className="w-14 h-8 text-center text-sm font-mono"
                          value={selected || ""}
                          onChange={(e) => updateBatchQty(batch.id, e.target.value, batch.quantity)}
                          placeholder="0"
                          data-testid={`batch-qty-input-${batch.id}`}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => increment(batch.id, batch.quantity)}
                          disabled={selected >= batch.quantity}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3 flex-col sm:flex-row gap-2">
          <div className="flex-1 text-sm text-muted-foreground">
            {totalSelected > 0 ? (
              <span>
                Selected: <span className="font-mono font-semibold text-[#9E6D5B]">{totalSelected}</span> unit{totalSelected > 1 ? "s" : ""}
                {Object.keys(selections).length > 1 && (
                  <span className="text-xs ml-1"> ({Object.keys(selections).length} batches)</span>
                )}
              </span>
            ) : (
              <span>Select quantity from at least one batch</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={totalSelected === 0}
              className="text-white"
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="batch-confirm-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
